import { useAuth } from './useAuth'

export const useApi = () => {
  const { user } = useAuth()

  const configApiDomain = process.env.REACT_APP_API_DOMAIN

  /**
   * Подстановочный параметр:
   *   %PATH% - из config.path
   */
  const endpoint = {
    ACCOUNT: '/account',
    ACCOUNT_COMPANY: '/account/company',
    ACCOUNT_COMPANY_DEACTIVATE: '/account/company/deactivate',
    ACCOUNT_COMPANY_ENUM: '/account/company/enum',
    ACCOUNT_COMPANY_LIST: '/account/company/list',
    ACCOUNT_ENUM: '/account/enum',
    ACCOUNT_FIND: '/account/find',
    ACCOUNT_LIST: '/account/list',
    ACCOUNT_PASSWORD: '/account/password',
    CERTIFICATE: '/certificate',
    CERTIFICATE_FREE_NUMBER: '/certificate/free_number',
    CERTIFICATE_LIST: '/certificate/list',
    CERTIFICATE_SERIES_ENUM: '/certificate_series/enum',
    COMPANY_ROLE_ENUM: '/company_role/enum',
    CONTACT: '/contact',
    CONTACT_LIST: '/contact/list',
    CONTACT_PATH_LIST: `/contact/%PATH%/list`,
    CONTACT_VERIFIED: '/contact/verified',
    CONTACT_EMAIL_VERIFY_SEND: '/contact/email/verify/send',
    COURSE: '/course',
    COURSE_LIST: '/course/list',
    CITY: '/city',
    CITY_LIST: '/city/list',
    DATA1C_ORDER_DELIVERYSERVICE_OPTION: '/data_1c/order/delivery_service/option',
    DATA1C_ORDER_DELIVERYTYPE_OPTION: '/data_1c/order/delivery_type/option',
    DATA1C_ORDER_ORGANIZATION_OPTION: '/data_1c/order/organization/option',
    DATA1C_ORDER_STATUS_OPTION: '/data_1c/order/status/option',
    DATA1C_ORGANIZATION_LIST: '/data_1c/organization/list',
    DATA1C_SALESORDERHEADER: '/data_1c/sales_order_header',
    DATA1C_SALESORDERHEADER_LIST: '/data_1c/sales_order_header/list',
    DATA1C_SALESORDERHEADER_UNPAID: '/data_1c/sales_order_header/unpaid',
    DATA1C_SALESORDERITEMS: '/data_1c/sales_order_items',
    DATA1C_SALESSTATS: '/data_1c/stats',
    PAYMENT_ROBOKASSA: '/robo',
    PAYMENT_ROBOKASSA_STATUS: '/robo/status',
    INSTRUCTOR: '/instructor',
    INSTRUCTOR_ADD: '/instructor/add',
    INSTRUCTOR_DEACTIVATE: '/instructor/deactivate',
    INSTRUCTOR_ENUM: '/instructor/enum',
    INSTRUCTOR_STATUS_ENUM: '/instructor/status/enum',
    INSTRUCTOR_LIST: '/instructor/list',
    INSTRUCTOR_MOVE: '/instructor/move',
    KNOWLEDGE_BASE: '/knowledge_base',
    KNOWLEDGE_BASE_FILE: '/knowledge_base/file',
    KNOWLEDGE_BASE_LIST: '/knowledge_base/list',
    MASTER: '/master',
    MASTER_ENUM: '/master/enum',
    MASTER_GROUP_ENUM: '/master/group/enum',
    MASTER_LEVEL_ENUM: '/master_level/enum',
    MASTER_LIST: '/master/list',
    MASTER_NOTE: '/master/note',
    MASTER_NOTE_LIST: '/master/note/list',
    MASTER_STATUS: '/master/status',
    MK_SUMMARY: '/report/mk_summary',
    PARENT_ENUM: '/parent/enum',
    PICTURE: '/picture',
    REPORT_INSTRUCTOR: '/report/instructor',
    REPORT_INSTRUCTOR_MONTH: '/report/instructor/month',
    REPORT_INSTRUCTOR_TOP: '/report/instructor/top',
    REPORT_INSTRUCTOR_YEAR: '/report/instructor/year',
    REPORT_PARENT: '/report/parent',
    RESALE: '/resale_order',
    RESALE_LIST: '/resale_order/list',
    RESALE_STATS_MASTER: '/resale_order/stats/master',
    RESALE_TYPE_ENUM: '/resale_order/type/enum',
    RESUME: '/resume',
    ROLE_ENUM: '/role/enum',
    SEMINAR: '/seminar',
    SEMINAR_LIST: '/seminar/list',
    SIGNUP: '/signup',
    SIGNUP_EMAIL: '/signup/email',
    SIGNUP_INSTRUCTOR: '/signup/instructor',
    SIGNUP_MASTER: '/signup/master',
    SIGNUP_OTP: '/signup/otp',
    SIGNUP_OTP_LIST: '/signup/otp/list',
    SIGNUP_PRIVACY_LIST: '/signup/privacy/list',
    SIGNUP_REGISTER: '/signup/register',
    FORUM_CONNECT_COMPLETE: '/forum/connect/complete',
    STORAGE: '/storage',
    STORAGE_FILE: '/storage/file',
    STORAGE_LIST: '/storage/list',
    TEAM: '/team',
    TEAM_LIST: '/team/list',
    VKPHOTO: '/vk_photo',
    WORK_TYPE_ENUM: '/work_type/enum',
  }

  /**
   * HTTP запрос с заголовком авторизации, получение объекта или blob
   *
   * @param method - get, post, put, delete
   * @param endpoint - относительный путь на сервере API
   * @param param - query-параметры запроса
   * @param config - дополнительные параметры запроса
   *          path - подстановка в путь на сервере
   *          body - тело запроса
   *          responseType = blob - получение blob
   *          downloadFilename - имя загружаемого файла, переданного в blob
   *          onError - пользовательское действие при ошибке
   * @returns {Promise<any|{}|Blob>}
   *          при ошибке reject текста ошибки
   */
  const request = async (method, endpoint, param = null, config = {}) => {
    const options = {
      method: method,
      headers: new Headers(),
    }
    if (user && user.hasOwnProperty('accessToken')) {
      options.headers.append('Authorization', 'Bearer ' + user.accessToken)
    }
    if (config.hasOwnProperty('body') && config.body) {
      if (typeof config.body === 'object' && !(config.body instanceof FormData)) {
        options.headers.append('Content-Type', 'application/json;charset=utf-8')
        options.body = JSON.stringify(config.body)
      } else options.body = config.body
    }
    const url =
      configApiDomain +
      endpoint.replace('%PATH%', (config && config.path) || '') +
      (param && Object.keys(param).length > 0 ? '?' + new URLSearchParams(param).toString() : '')
    try {
      const response = await fetch(url, options)
      if (response.ok) {
        let resData
        if (config.responseType === 'blob') {
          const blob = await response.blob()
          if (blob.type == 'application/json') {
            resData = JSON.parse(await blob.text())
          } else if (config.hasOwnProperty('downloadFilename')) {
            downloadFile(blob, config.downloadFilename)
            return
          } else return blob
        } else resData = await response.json()
        if (resData.error === 0) {
          return resData.data
        } else {
          if (config.onError) {
            config.onError()
            return Promise.reject()
          } else {
            return Promise.reject({
              message: resData.message || `Ошибка запроса ${resData.error}`,
              status: response.status,
            })
          }
        }
      } else {
        if (config.onError) {
          config.onError()
          return Promise.reject()
        } else {
          const msg = await response.text()
          return Promise.reject({
            message: msg || `Ошибка запроса ${response.status}`,
            status: response.status,
          })
        }
      }
    } catch (e) {
      if (config.onError) config.onError()
      return Promise.reject({
        message: 'Общая ошибка запроса',
        status: 0,
      })
    }
  }

  const get = (endpoint, param = null, config = {}) => {
    return request('get', endpoint, param, config)
  }

  const post = (endpoint, param = null, config = {}) => {
    return request('post', endpoint, param, config)
  }

  const del = (endpoint, param = null, config = {}) => {
    return request('delete', endpoint, param, config)
  }

  const getPicture = (filter) => {
    return new Promise((resolve, reject) => {
      request('get', endpoint.PICTURE, filter, {
        responseType: 'blob',
        onError: () => {},
      })
        .then((data) => {
          const urlCreator = window.URL || window.webkitURL
          const imageUrl = urlCreator.createObjectURL(data)
          resolve(imageUrl)
        })
        .catch(() => resolve(null))
    })
  }

  const downloadFile = (blob, fileName) => {
    const urlCreator = window.URL || window.webkitURL
    const url = urlCreator.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = fileName
    a.style.display = 'none'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    urlCreator.revokeObjectURL(url)
  }

  return {
    endpoint,
    get,
    post,
    del,
    getPicture,
  }
}
